import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import Navbar from "./Navbar";
import Footer from "./Footer";
import "../Style/Password_warning.css";
import login_image from "../Images/Login-image.png";
import logo from "../Images/warning_sign.jpg";
import PasswordStrength, { getPasswordRules } from "../components/PasswordStrength";
import { updatePassword } from "../Services/authService";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function ResetPassword() {
  const location = useLocation();
  const navigate = useNavigate();

  const email = location.state?.email || "";
  const resetToken = location.state?.resetToken || "";

  const [capsLockOn, setCapsLockOn] = useState(false);
  const [issubmit, setIssubmit] = useState(false);

  useEffect(() => {
    if (!email || !resetToken) {
      toast.warning("Session invalid or expired. Please start password reset again.");
      navigate("/forgot-password", { replace: true });
    }
  }, [email, resetToken, navigate]);

  const handleKeyDown = (event) => {
    if (event.getModifierState("CapsLock")) {
      setCapsLockOn(true);
    } else {
      setCapsLockOn(false);
    }
  };

  const handleKeyUp = (event) => {
    if (!event.getModifierState("CapsLock")) {
      setCapsLockOn(false);
    }
  };

  const validationSchema = Yup.object().shape({
    newPassword: Yup.string()
      .required("New password is required")
      .min(8, "Password must be at least 8 characters")
      .test(
        "password-strength",
        "Password must contain uppercase, lowercase, number, and special character",
        (value) => {
          if (!value) return false;
          const rules = getPasswordRules(value);
          return rules.every((r) => r.valid);
        }
      ),
    confirmPassword: Yup.string()
      .required("Please confirm your new password")
      .oneOf([Yup.ref("newPassword"), null], "Passwords must match"),
  });

  const formik = useFormik({
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setIssubmit(true);
      try {
        const result = await updatePassword(email, resetToken, values.newPassword);
        toast.success(result.message || "Password reset successfully! Redirecting to login...");
        setTimeout(() => {
          navigate("/login", { state: { resetSuccess: true } });
        }, 1500);
      } catch (error) {
        toast.error(error.message || "Failed to update password.");
      } finally {
        setSubmitting(false);
        setIssubmit(false);
      }
    },
  });

  if (!email || !resetToken) return null;

  return (
    <>
      <Navbar />
      <div className="Login-form">
        <div className="login-image-container">
          <img src={login_image} alt="reset-password" className="login-image" />
        </div>
        <form onSubmit={formik.handleSubmit} className="form-container">
          <div className="contents">
            <span className="Login-heading">Set New Password</span>
            <span className="Login-content">
              Create a new secure password for <strong style={{ color: "#5e3bee" }}>{email}</strong>.
            </span>
          </div>

          <div className="login-form-main-container">
            <div className="login-form-content">
              <div className="form-group">
                <input
                  name="newPassword"
                  type="password"
                  placeholder="New Password"
                  className={
                    "form-control form-control-2" +
                    (formik.errors.newPassword && formik.touched.newPassword
                      ? " is-invalid"
                      : "")
                  }
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.newPassword}
                  onKeyDown={handleKeyDown}
                  onKeyUp={handleKeyUp}
                />
                {capsLockOn && (
                  <div className={"warning"}>
                    <strong className={"h1"}>
                      <img src={logo} style={{ height: "40px", width: "40px" }} alt="warning" />
                      Caps Lock is On!
                    </strong>
                    <p className={"h2"}>
                      Having Caps Lock on may cause you to enter your password incorrectly.
                    </p>
                  </div>
                )}
                <div className="invalid-feedback">
                  {formik.errors.newPassword && formik.touched.newPassword
                    ? formik.errors.newPassword
                    : null}
                </div>

                {/* Password Strength Meter & Checklist */}
                <PasswordStrength password={formik.values.newPassword} />
              </div>

              <div className="form-group" style={{ marginTop: "12px" }}>
                <input
                  name="confirmPassword"
                  type="password"
                  placeholder="Confirm New Password"
                  className={
                    "form-control form-control-2" +
                    (formik.errors.confirmPassword && formik.touched.confirmPassword
                      ? " is-invalid"
                      : "")
                  }
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.confirmPassword}
                />
                <div className="invalid-feedback">
                  {formik.errors.confirmPassword && formik.touched.confirmPassword
                    ? formik.errors.confirmPassword
                    : null}
                </div>
              </div>
            </div>

            <div className="buttons buttons-2" style={{ marginTop: "24px" }}>
              <div className="form-group">
                <button
                  type="submit"
                  className="login-buttons"
                  disabled={issubmit || formik.isSubmitting}
                >
                  <div className="btn-content">
                    <span>{issubmit ? "Updating..." : "Reset Password"}</span>
                  </div>
                </button>
              </div>

              <div className="form-group" style={{ textAlign: "center", marginTop: "12px" }}>
                <Link to="/login" style={{ color: "#5e3bee", textDecoration: "none", fontSize: "14px", fontWeight: "500" }}>
                  ← Cancel and Back to Login
                </Link>
              </div>
            </div>
          </div>
        </form>
      </div>

      <Footer />
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </>
  );
}
